import React, { useMemo, useState, useEffect } from 'react';
import MuiPaper from '@mui/material/Paper';
import styled from 'styled-components/macro';
import { spacing } from '@mui/system';
import { formatDecimal } from '../../services/utils/format-numbers';
import { Button, Typography, TableCell } from '@mui/material';
import DataTable from '../../components/DataTable';
import { useAuth } from '../../services/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import TimePeriodLabel from '../../components/TimePeriodLabel';
import { PAGE_SIZE, MIN_PAGE_SIZE } from '../../services/utils/units-constants';
import api from '../../Middleware/api/api';
import LogoLoader from '../../components/LogoLoader';
import { TextField } from 'formik-mui';
import { useTokenUtils } from '../../services/utils/tokenUtils';
import AllocateUnknownSessionDialog from './Components/AllocateUnknownSessionDialog';
import Feedback from '../../components/DataTable/Feedback';
const Paper = styled(MuiPaper)(spacing);
function EnhancedTable({
  startDate,
  endDate,
  filterParam,
  filterValue,
  changeFlag,
}) {
  const { t, i18n } = useTranslation();

  const { token } = useAuth();
  const [fetchingData, setFetchingData] = useState(false);
  const [chargingHistory, setChargingHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE[0]);
  const [loading, setLoading] = useState(false);
  const [newFilters, setNewFilters] = useState([]);
  const { isAdminRole } = useTokenUtils();
  const [allocateRow, setAllocateRow] = useState(null);
  const [allocationFlag, setAllocationFlag] = useState(0);
  const [allocationFeedback, setAllocationFeedback] = useState(null);
  const isValidDate = (dateString) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };
  const params = useMemo(() => {
    const urlParams = new URLSearchParams();
    if (
      (filterParam == 'transactionId' || filterParam == 'endCustomerId') &&
      filterValue != undefined &&
      filterValue != null &&
      filterValue != ''
    ) {
      urlParams.set(filterParam, filterValue);
    }
    // OLISYS-5007 review: the unattributed-energy filters deliberately carry no date range. A gap's
    // start is when the energy began, which can be weeks before anyone noticed it, so keeping the
    // default last-seven-days window would hide exactly the rows these filters exist to collect.
    const unattributedFilter =
      filterParam == 'unattributedOpen'
        ? 'OPEN'
        : filterParam == 'unattributedAllocated'
          ? 'ALLOCATED'
          : null;
    if (unattributedFilter) {
      urlParams.set('unattributed', unattributedFilter);
    } else if (isValidDate(startDate) && isValidDate(endDate)) {
      urlParams.set('startDate', startDate);
      urlParams.set('endDate', endDate);
    }

    if (newFilters && typeof newFilters === 'object') {
      Object.entries(newFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          urlParams.set(key, values.join(','));
        }
      });
    }
    return urlParams;
  }, [endDate, startDate, filterParam, filterValue, newFilters]);

  const getCharginHistory = async (seletedPageSize, currentPageVal) => {
    setFetchingData(true);
    setLoading(true);
    let urlParams = params;
    urlParams.set('page', currentPageVal);
    urlParams.set('size', seletedPageSize);
    api
      .fethcChargingHistory(token, urlParams)
      .then((response) => {
        if (response?.data?.data == null) {
          setChargingHistory([]);
          setFetchingData(false);
          setLoading(false);
        } else {
          setTotalRecords(response?.data?.data?.totalNumberOfRecords);
          setChargingHistory(
            response?.data?.data?.response?.sort((a, b) => {
              return (
                new Date(b.startDate).getTime() -
                new Date(a.startDate).getTime()
              );
            })
          );
          setFetchingData(false);
          setLoading(false);
        }
      })
      .catch((error) => {
        setChargingHistory([]);
        setFetchingData(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (fetchingData != true) {
      getCharginHistory(pageSize, currentPage);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (changeFlag != null) {
      setCurrentPage(0);
      setPageSize(MIN_PAGE_SIZE);
      getCharginHistory(MIN_PAGE_SIZE, 0);
    }
  }, [changeFlag]);

  // Deliberately a local counter rather than reusing changeFlag: that one resets to page 0 and the minimum
  // page size, which would throw the admin off the very row they just acted on.
  useEffect(() => {
    if (allocationFlag > 0) {
      getCharginHistory(pageSize, currentPage);
    }
  }, [allocationFlag]);

  const columns = useMemo(
    () => [
      {
        name: 'transactionId',
        label: t('LBLTranId'),
        options: {
          filter: true,
          sort: false,
          customFilterListRender: (v) => t('LBLTranId') + `: ${v}`, // Show filter value in UI
          filterType: 'textField', // Use built-in text filtering
          customHeadRender: () => (
            <TableCell
              variant="head"
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 2,
              }}
            >
              {t('LBLTranId')}
            </TableCell>
          ),
        },
      },
      {
        name: 'endCustomerId',
        label: t('searchTypeEndCustomerId'),
        options: {
          filter: true,
          sort: false,
          customFilterListRender: (v) =>
            t('searchTypeEndCustomerId') + `: ${v}`, // Show filter value in UI
          filterType: 'textField', // Use built-in text filtering
          customHeadRender: () => (
            <TableCell
              variant="head"
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('searchTypeEndCustomerId')}
            </TableCell>
          ),
        },
      },
      {
        name: 'customerFullName',
        label: t('LBLUser'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => {
            return <Typography align="left">{value || ''}</Typography>;
          },
          customHeadRender: () => (
            <TableCell
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLUser')}
            </TableCell>
          ),
        },
      },
      {
        name: 'parkingLotAndAddress',
        label: t('LBLAddress'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => {
            let valuedataParking = value?.parkingLot?.toString();
            let valuedata = value?.locationAddress;
            let parkingData = t('LBLParkingLotNum') + ': ' + valuedataParking;
            let cof = valuedata?.careOf ? `c/o ${valuedata?.careOf}, ` : '';
            let finalAddress =
              cof +
              valuedata?.street +
              ', ' +
              valuedata?.zipCode +
              ' ' +
              valuedata?.city;
            if (!value?.parkingLot) {
              if (valuedata) {
                return (
                  <Typography align="left">{finalAddress || ''}</Typography>
                );
              } else {
                return null;
              }
            } else {
              return valuedata
                ? `${parkingData}, ${finalAddress || ''}`
                : parkingData;
            }
          },
          customHeadRender: () => (
            <TableCell
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLAddress')}
            </TableCell>
          ),
        },
      },
      {
        name: 'authTypeAndRfIdTag',
        label: t('LBLAuth'),
        options: {
          filter: false,
          sort: false,
          customBodyRender(value) {
            if (!value) return null;
            let authType = value?.authType;
            if (authType !== 'APP') {
              let rfidSerialId = value?.rfidSerialId?.toString();
              let rfidTag = value?.rfidTag?.toString();

              let rfidDisplay =
                rfidSerialId && rfidSerialId.trim() !== ''
                  ? rfidSerialId
                  : rfidTag;
              return (
                <Typography align="left">
                  {authType} ({rfidDisplay})
                </Typography>
              );
            } else {
              return <Typography align="left">{authType}</Typography>;
            }
          },
          customHeadRender: () => (
            <TableCell
              align="center"
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLAuth')}
            </TableCell>
          ),
        },
      },
      {
        name: 'chargingRequestStatusMapping',
        label: t('LBLChargingStatus'),
        options: {
          filter: true,
          sort: false,
          filterType: 'multiselect',
          filterOptions: {
            names: ['Pending', 'InProgress', 'Completed', 'Failed'], // here
          },
          customBodyRender: (value) => {
            if (!value) return null;
            return (
              <Typography align="center" color={value.colourCode}>
                {t(value.enumName)}
              </Typography>
            );
          },
          customHeadRender: () => (
            <TableCell
              variant="head"
              sx={{
                textAlign: 'center',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLChargingStatus')}
            </TableCell>
          ),
        },
      },

      {
        name: 'timePeriod',
        label: t('lblPeriod'),
        options: {
          filter: false,
          sort: false,
          // eslint-disable-next-line no-unused-vars
          customBodyRender: (value) => {
            return (
              <>
                <TimePeriodLabel
                  align="center"
                  startDate={value.startDate}
                  endDate={value.endDate}
                  lastUpdateTimestamp={value.lastUpdateTimestamp}
                  status={value.status.toLowerCase()}
                />
              </>
            );
          },
          customHeadRender: () => (
            <TableCell
              align="center"
              variant="head"
              width={'180px'}
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('lblPeriod')}
            </TableCell>
          ),
        },
      },
      {
        name: 'endDate',
        label: t('lblPeriod'),
        options: {
          filter: false,
          sort: false,
          display: false,
          customHeadRender: () => (
            <TableCell
              align="right"
              variant="head"
              width={'180px'}
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('LBLEndDate')}
            </TableCell>
          ),
        },
      },
      {
        name: 'energyCharged',
        label: t('LBLAmountenergyCharged'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => {
            let valueData = formatDecimal(value);
            if (i18n.language === 'en') {
              valueData = valueData.replace(/,/g, '.');
            }
            return (
              <Typography align="right" paddingRight={2}>
                {value !== null ? valueData + ' kWh' : ''}
              </Typography>
            );
          },
          customHeadRender: () => (
            <TableCell
              align="right"
              variant="head"
              sx={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              {t('LBLAmountenergyCharged')}
            </TableCell>
          ),
        },
      },
      {
        name: 'rfidTag',
        label: t('lblPeriod'),
        options: {
          filter: false,
          sort: false,
          display: false,
          // eslint-disable-next-line no-unused-vars
          customBodyRender: (value) => {
            return (
              <>
                <Typography align="center">{value}</Typography>
              </>
            );
          },
          customHeadRender: () => (
            <TableCell
              align="center"
              variant="head"
              width={'180px'}
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {t('lblPeriod')}
            </TableCell>
          ),
        },
      },
      {
        name: 'locationAddress',
        label: 'locationAddress',
        options: {
          filter: false,
          sort: false,
          display: false,
          customBodyRender: (value) => {
            return <Typography align="left">{value}</Typography>;
          },
          customHeadRender: () => (
            <TableCell
              variant="head"
              sx={{
                textAlign: 'left',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
              }}
            >
              {'locationAddress'}
            </TableCell>
          ),
        },
      },
      // OLISYS-5007: the admin action on an Unknown Charging Session. Gated on the raw backend status, never
      // on the translated label, and only rendered for admins - the endpoint enforces the same role.
      ...(isAdminRole
        ? [
            {
              name: 'allocateAction',
              label: ' ',
              options: {
                filter: false,
                sort: false,
                customBodyRender: (value) =>
                  value?.status === 'UNKNOWN_ID' ? (
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => setAllocateRow(value)}
                    >
                      {t('BTNAllocateUnknownSession')}
                    </Button>
                  ) : null,
                customHeadRender: () => (
                  <TableCell
                    variant="head"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'white',
                    }}
                  >
                    {' '}
                  </TableCell>
                ),
              },
            },
          ]
        : []),
    ],
    [filterParam, isAdminRole, t]
  );

  const renderChargingHistoryData = useMemo(() => {
    const rawChargingHistory = chargingHistory;
    return rawChargingHistory
      ?.sort((entry1, entry2) => entry2.startDate - entry1.startDate)
      .map((entry) => {
        const { ...rawChargingHistory } = entry;
        const newEntry = {
          ...rawChargingHistory,
          timePeriod: {
            startDate: rawChargingHistory.startDate,
            endDate: rawChargingHistory.endDate,
            lastUpdateTimestamp: rawChargingHistory.lastUpdateTimestamp,
            status: rawChargingHistory.status.toLowerCase(),
          },
          parkingLotAndAddress: {
            parkingLot: rawChargingHistory.parkingLot,
            locationAddress: rawChargingHistory?.locationAddress || '',
          },
          authTypeAndRfIdTag: {
            authType: rawChargingHistory.authType,
            rfidTag: rawChargingHistory.rfidTag,
            rfidSerialId: rawChargingHistory.rfidSerialId,
          },
          // The allocate action keys on crId. transactionId is only a fallback, so the dashboard keeps
          // working whichever of the two services deploys first.
          allocateAction: {
            crId: rawChargingHistory.crId || rawChargingHistory.transactionId,
            status: rawChargingHistory.status,
          },
        };
        return newEntry;
      });
  }, [chargingHistory]);

  return (
    <Paper>
      <>
        <LogoLoader loading={loading} />
        <Paper
          variant="outlined"
          sx={{
            width: '100%',
            '& .MuiTableCell-head:not(:nth-of-type(2)) button': {
              mx: 0,
            },
          }}
        >
          <DataTable
            data={renderChargingHistoryData}
            columns={columns}
            options={{
              filter: false,
              textLabels: {
                body: {
                  noMatch: fetchingData ? t('LBLLOading') : t('LBLNoData'),
                },
              },
              currentPage: currentPage,
              totalRecords: totalRecords,
              onTableChange: (action, tableState) => {
                if (action === 'filterChange') {
                  const newFilters = {};
                  tableState.filterList.forEach((filter, index) => {
                    if (filter.length > 0) {
                      newFilters[tableState.columns[index].name] = filter;
                    }
                  });
                  setNewFilters(newFilters);
                } else if (action === 'resetFilters') {
                  setNewFilters([]);
                } else {
                  console.log('action ', action);
                }
              },
            }}
            getData={(seletedPageSize, currentPageVal) => {
              setCurrentPage(currentPageVal);
              setPageSize(seletedPageSize);
            }}
          />
        </Paper>
        <AllocateUnknownSessionDialog
          open={Boolean(allocateRow)}
          crId={allocateRow?.crId}
          handleClose={() => setAllocateRow(null)}
          onAllocated={(contractKey) => {
            setAllocateRow(null);
            // No contract key means the allocation did not happen (the row was already taken) - refresh the
            // list, but do not claim success.
            if (contractKey) setAllocationFeedback(contractKey);
            setAllocationFlag((prev) => prev + 1);
          }}
        />
        <Feedback
          open={Boolean(allocationFeedback)}
          title={t('LBLAllocateSuccessTitle')}
          message={t('MSGAllocateSuccess', { contractKey: allocationFeedback })}
          severity="success"
          handleClose={() => setAllocationFeedback(null)}
        />
      </>
    </Paper>
  );
}
export default EnhancedTable;
